import React, { useState, useEffect, useRef } from 'react';
import InfoCard from '../components/InfoCard';
import './AboutPage.css';

// Hook to check if an element is visible on the screen
function useOnScreen(ref, threshold = 0.1) {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, threshold]);

  return isIntersecting;
}

function AboutPage() {
  // References for sections
  const introRef = useRef(null);
  const visionRef = useRef(null);
  const missionRef = useRef(null);
  const objectivesRef = useRef(null);
  const benefitsRef = useRef(null);
  const organizationRef = useRef(null);
  const collaborationRef = useRef(null);
  const facilitiesRef = useRef(null);

  // Visibility states using the custom hook
  const introVisible = useOnScreen(introRef);
  const visionVisible = useOnScreen(visionRef);
  const missionVisible = useOnScreen(missionRef);
  const objectivesVisible = useOnScreen(objectivesRef);
  const benefitsVisible = useOnScreen(benefitsRef);
  const organizationVisible = useOnScreen(organizationRef);
  const collaborationVisible = useOnScreen(collaborationRef);
  const facilitiesVisible = useOnScreen(facilitiesRef);

  // State for each facility's image index
  const [simulatorIndex, setSimulatorIndex] = useState(0);
  const [classroomIndex, setClassroomIndex] = useState(0);
  const [dormitoryIndex, setDormitoryIndex] = useState(0);

  // Images for each facility
  const simulatorImages = [
    "images/IMG-20241112-WA0020-modified.jpg",
    "images/IMG-20241112-WA0022-modified.jpg",
    "images/IMG-20241112-WA0029-modified.jpg",
    "images/IMG_9826-modified.JPG",
    "images/IMG-20241112-WA0026-modified.JPG",
  ];
  const classroomImages = [
    "images/IMG-20241112-WA0035.jpg",
    "images/IMG-20241112-WA0027.jpg",
    "images/IMG-20241112-WA0037.jpg"
  ];
  const dormitoryImages = [
    "images/i-modified.jpg",
    "images/IMG-20241112-WA0031.jpg",
    "images/IMG_9832-modified.JPG",
    "images/IMG_9835-modified.JPG",
    "images/IMG_9830.JPG",
   
  ];

  // Automatically cycle through carousel images
  useEffect(() => {
    const interval = setInterval(() => {
      setSimulatorIndex((prevIndex) => (prevIndex + 1) % simulatorImages.length);
      setClassroomIndex((prevIndex) => (prevIndex + 1) % classroomImages.length);
      setDormitoryIndex((prevIndex) => (prevIndex + 1) % dormitoryImages.length);
    }, 5000); // Change images every 5 seconds

    return () => clearInterval(interval); // Cleanup interval on unmount
  }, [simulatorImages.length, classroomImages.length, dormitoryImages.length]);

  // Carousel functions
  const handleNext = (setIndex, images) => {
    setIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handlePrev = (setIndex, images) => {
    setIndex((prevIndex) => (prevIndex - 1 + images.length) % images.length);
  };

  return (
    <div className="about-container">
      <section
        ref={introRef}
        className={`about-intro ${introVisible ? 'visible' : ''}`}
      >
        <h2>About RDTC</h2>
<InfoCard 
  title="Introduction to RDTC"
  content={
    <>
      The <strong>Regional Driving Training Center (RDTC)</strong> in Singawal, Ajmer, has been established by the <strong>Ministry of Road Transport & Highways (MoRTH)</strong>, Government of India, under the <strong>IDTR/RDTC </strong>scheme of the 14th Financial Circle (2017–18 to 2020–21).
      <br /><br />
      Approved and sanctioned by MoRTH on <strong>March 10, 2024</strong>. <strong>The RDTC Singawal stands as India’s first RDTC.</strong>  The project was developed under a <strong>Public-Private Partnership (PPP)</strong> model in collaboration with the Rajasthan Sadak Suraksha Society (RSSS), a prominent NGO focused on road safety in Rajasthan. Since its inception, RSSS has educated over one million youth and trained 85,000 “Road Safety Ambassadors.”
      <br /><br />
      The center was successfully completed and became fully operational on <strong>21 March 2024</strong>. This state-of-the-art facility is dedicated to enhancing road safety through comprehensive and professional driver training programs. It aims to elevate driving standards, reduce road accidents, and contribute to safer road practices across the region, aligning with the government’s ongoing efforts to improve road safety nationwide.
      <br /><br />
      The management and operations of <strong>RDTC</strong> are overseen by <strong>Vahan Chalak Kaushal Vikas Sansthan (VCKVS)</strong>, a non-governmental organization (NGO) registered under the Rajasthan Society Registration Act, 1958. VCKVS is recognized under Sections 80G and 12A of the Income Tax Act, 1961, and is also registered under the Trust Act and CSR1.
      <br /><br />
      <strong>VCKVS</strong> has been established to oversee the construction, management, and operational activities of the RDTC. The organization includes representatives from<strong> MoRTH, the Transport & Road Safety Department (Government of Rajasthan),</strong>and <strong> the Rajasthan Sadak Suraksha Society (RSSS).</strong> 
    </>
  }
  isButtonVisible={false}
  className="rdtc-card"
/>


      </section>

      <section
  ref={missionRef}
  className={`about-mission ${missionVisible ? 'visible' : ''}`}
>
  <h3>Our Mission</h3>
  <p>
    To enhance road safety by delivering comprehensive, high-quality driver training that
    fosters responsible, skilled, and safety-conscious drivers. RDTC is committed to
    reducing road accidents through structured education, hands-on experience, and the
    promotion of ethical driving practices, while empowering individuals with the skills
    necessary for sustainable employment in the transportation sector.
  </p>
</section>
<div className="training-partners-section">
  <h2>We Are Training Partners</h2>
  <p>
  "We are proud to be Training Partners with esteemed organizations, including the following Sector Skill Councils:
    <strong> ASDC</strong>, <strong>LSC</strong>, <strong>NSDC</strong>, and others."
  </p>
  <div className="partner-logos">
    <a href="https://www.asdc.org.in/" target="_blank" rel="noopener noreferrer">
      <img src="/images/1656312317Logo.png...png" alt="ASDC Logo" className="partner-logo" />
    </a>
    <a href="https://lsc-india.com/" target="_blank" rel="noopener noreferrer">
    <a href="" target="_blank" rel="noopener noreferrer"></a>
    <img src="/images/Logistics-Sector-Skill-Council-Logo-2.png" alt="LSC Logo" className="partner-logo" />
    </a>
    <a href="https://dbskills.com/" target="_blank" rel="noopener noreferrer">
    <img src="/images/db-skills-logo-removebg-preview.png" alt="NSDC Logo" className="partner-logo" />
    </a>
    <a href="https://www.tuv.com/world/en/" target="_blank" rel="noopener noreferrer">
    <img src="/images/TUV_Rheinland_Logo.png" alt="TUV Logo" className="partner-logo" />
    </a>
    <a href="https://www.skillindiadigital.gov.in/" target="_blank" rel="noopener noreferrer">
    <img src="/images/Skill-Logo-2048x979.png" alt="NSDC Logo" className="partner-logo" />
    </a>
    <a href="https://www.nsdcinternational.com/" target="_blank" rel="noopener noreferrer">
    <img src="/images/NSDC-Logo-2048x979.png" alt="NSDC Logo" className="partner-logo" />
    </a>
    <a href="https://takamolholding.com/en" target="_blank" rel="noopener noreferrer">
    <img src="/images/takamul.png" alt="NSDC Logo" className="partner-logo" />
    </a>
  </div>
</div>
<section
  ref={objectivesRef}
  className={`about-objectives ${objectivesVisible ? 'visible' : ''}`}
>
  <h3>Objectives</h3>
  <ul>
    <li>Road safety education and awareness in order to reduce accidents.</li>
    <li>To impart basic driving training on technical and practical aspects.</li>
    <li>To impart skill driving training for LCV, MCV, HMV, buses, and 2-wheelers.</li>
    <li>To impart refresher training and traffic offender training.</li>
    <li>To provide first aid and first responder training.</li>
    <li>To offer driving license guidelines and certificate courses.</li>
    <li>To conduct road safety training programs in schools and colleges.</li>
    <li>To make youth employable and self-employed.</li>
    <li>To provide employment opportunities in India as well as abroad in the driving field.</li>
  </ul>
</section>

      <section
        ref={facilitiesRef}
        className={`about-facilities ${facilitiesVisible ? 'visible' : ''}`}
      >
        <h3>Facilities</h3>
        <div className="facilities-list">
          <div className="facility-item">
            <h4>Advanced Simulators</h4>
            <p>For light motor vehicles (LMV), heavy motor vehicles (HMV), and two-wheelers, providing real-world training experiences.</p>
            <div className="carousel">
              <button className="carousel-button left" onClick={() => handlePrev(setSimulatorIndex, simulatorImages)}>‹</button>
              <img src={simulatorImages[simulatorIndex]} alt="Simulator" className="carousel-image" />
              <button className="carousel-button right" onClick={() => handleNext(setSimulatorIndex, simulatorImages)}>›</button>
            </div>
          </div>
          <div className="facility-item">
            <h4>Classrooms</h4>
            <p>Equipped with advanced tools for theoretical instruction, ensuring that all drivers learn both safe driving techniques and road safety principles.</p>
            <div className="carousel">
              <button className="carousel-button left" onClick={() => handlePrev(setClassroomIndex, classroomImages)}>‹</button>
              <img src={classroomImages[classroomIndex]} alt="Classroom" className="carousel-image" />
              <button className="carousel-button right" onClick={() => handleNext(setClassroomIndex, classroomImages)}>›</button>
            </div>
          </div>
          <div className="facility-item">
            <h4>Dormitories/Facilities</h4>
            <p>Comfortable and secure accommodations for out-of-town trainees.</p>
            <div className="carousel">
              <button className="carousel-button left" onClick={() => handlePrev(setDormitoryIndex, dormitoryImages)}>‹</button>
              <img src={dormitoryImages[dormitoryIndex]} alt="Dormitory" className="carousel-image" />
              <button className="carousel-button right" onClick={() => handleNext(setDormitoryIndex, dormitoryImages)}>›</button>
            </div>
          </div>
        </div>
      </section>

      <section
        ref={benefitsRef}
        className={`about-benefits ${benefitsVisible ? 'visible' : ''}`}
      >
        <h3>Benefits</h3>
        <ul>
          <li>Access to expert trainers with years of experience.</li>
          <li>Certified courses recognized by government agencies.</li>
          <li>Advanced training methods using simulators and real-world scenarios.</li>
          <li>State-of-the-art infrastructure for an immersive learning experience.</li>
          <li>Comprehensive road safety education and awareness.</li>
        </ul>
      </section>
      <div className="horizontal-sections">
<section
  ref={visionRef}
  className={`about-vision ${visionVisible ? 'visible' : ''}`}
>
  <h3>Our Vision</h3>
  <p>
    To create a safer road environment by developing skilled, responsible, and
    knowledgeable drivers through high-quality training, pre-licensing preparation,
    and structured certification programs. RDTC envisions a future where every driver
    is not only licensed but also road-ready, alert, and responsible.
  </p>
</section>
        <section
          ref={organizationRef}
          className={`about-organization ${organizationVisible ? 'visible' : ''}`}
        >
          <h3>Organization</h3>
          <p>We are supported by the government of India and our key partners, which include leading industry players in the transportation sector.</p>
        </section>

        <section
        ref={collaborationRef}
        className={`about-collaboration ${collaborationVisible ? 'visible' : ''}`}
      >
        <h3>Collaborations</h3>
        <p>We work in partnership with various organizations dedicated to road safety and driver education, both on a national and regional scale.</p>
      </section>
      </div>

    </div>
  );
}

export default AboutPage;
